/**
 * Google Tag Manager + Google Consent Mode v2.
 * GTM is NOT loaded until the visitor grants analytics or marketing consent.
 */
export const GTM_ID = "GTM-NFQ7JDMT";

type ConsentFlags = { analytics: boolean; marketing: boolean };

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

// gtag must push the `arguments` object (not an array) for Consent Mode to work.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  ensureDataLayer().push(arguments);
}

let defaultsSet = false;
let gtmLoaded = false;

export const isGTMLoaded = () => gtmLoaded;

export const setConsentDefaults = () => {
  if (defaultsSet || typeof window === "undefined") return;
  defaultsSet = true;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  gtag("set", "ads_data_redaction", true);
};

const loadGTM = () => {
  if (gtmLoaded || typeof document === "undefined") return;
  gtmLoaded = true;
  ensureDataLayer().push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
};

const clearGoogleCookies = () => {
  const host = window.location.hostname;
  const domains = ["", host, `.${host.replace(/^www\./, "")}`];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0]?.trim();
    if (!name || !/^(_ga|_gid|_gat|_gcl|_fbp|_uet)/.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
};

export const applyConsent = (consent: ConsentFlags | null) => {
  if (typeof window === "undefined") return;
  setConsentDefaults();
  const a = !!consent?.analytics;
  const m = !!consent?.marketing;
  gtag("consent", "update", {
    analytics_storage: a ? "granted" : "denied",
    ad_storage: m ? "granted" : "denied",
    ad_user_data: m ? "granted" : "denied",
    ad_personalization: m ? "granted" : "denied",
  });
  if (a || m) {
    loadGTM();
  } else {
    clearGoogleCookies();
  }
};

export type LeadEvent = {
  form: string;
  division: "retail" | "industrial" | "medical";
  lang: string;
  tipo_espacio?: string | null;
  num_ubicaciones?: string | null;
  plazo?: string | null;
  request_type?: string | null;
};

/** Pushes a lead conversion to GTM. Only sent to Google if consent was granted. No personal data. */
export const trackLead = ({ form, division, lang, tipo_espacio, num_ubicaciones, plazo, request_type }: LeadEvent) => {
  if (typeof window === "undefined") return;
  ensureDataLayer().push({
    event: "generate_lead",
    form_name: form,
    lead_division: division,
    language: lang,
    tipo_espacio: tipo_espacio ?? undefined,
    num_ubicaciones: num_ubicaciones ?? undefined,
    plazo: plazo ?? undefined,
    request_type: request_type ?? undefined,
  });
};

export type ContactChannel = "whatsapp" | "email" | "phone";

/** Channel of a contact link, or null if it is not one. */
export const contactChannelFromHref = (href: string): ContactChannel | null => {
  const h = href.trim().toLowerCase();
  if (h.startsWith("https://wa.me") || h.startsWith("http://wa.me") || h.includes("api.whatsapp.com") || h.startsWith("whatsapp:")) return "whatsapp";
  if (h.startsWith("mailto:")) return "email";
  if (h.startsWith("tel:")) return "phone";
  return null;
};

/** Same consent pattern as generate_lead: dataLayer only, GTM forwards it only with consent. */
export const trackContactClick = (channel: ContactChannel, page: string) => {
  if (typeof window === "undefined") return;
  ensureDataLayer().push({ event: "contact_click", contact_channel: channel, page_path: page });
};
